import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { cleanAssistantText } from "../utils/assistantText";
import { shouldSubmitOnEnter } from "../utils/inputDevice";

function fmtTime(value) {
  if (!value) return "";
  const raw = String(value);
  const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jakarta",
  }).format(date);
}

async function copyToClipboard(value) {
  const text = String(value || "");
  if (!text) return false;

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {}
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
}

function CopyButton({ text, title = "Salin", variant = "dark" }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => window.clearTimeout(timeoutRef.current);
  }, []);

  async function handleCopy() {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopied(true);
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setCopied(false), 1600);
  }

  const idleClass = variant === "light"
    ? "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
    : "text-gray-400 hover:bg-white/10 hover:text-white";

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
        copied
          ? "bg-[#10a37f]/15 text-[#10a37f]"
          : idleClass
      }`}
      title={copied ? "Tersalin" : title}
      aria-label={copied ? "Tersalin" : title}
    >
      <span className="material-symbols-outlined text-[17px]">
        {copied ? "check" : "content_copy"}
      </span>
    </button>
  );
}

// Simple inline formatter (bold, inline code only — safe, no innerHTML)
function InlineText({ text }) {
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining) {
    const boldIdx = remaining.indexOf("**");
    const codeIdx = remaining.indexOf("`");
    const first = [boldIdx, codeIdx].filter((i) => i !== -1);
    const min = first.length ? Math.min(...first) : -1;

    if (min === -1) { parts.push(<span key={key++} className="break-words">{remaining}</span>); break; }

    if (min > 0) parts.push(<span key={key++} className="break-words">{remaining.slice(0, min)}</span>);

    if (min === boldIdx) {
      const end = remaining.indexOf("**", boldIdx + 2);
      if (end === -1) { parts.push(<span key={key++} className="break-words">{remaining}</span>); break; }
      parts.push(<strong key={key++} className="font-semibold">{remaining.slice(boldIdx + 2, end)}</strong>);
      remaining = remaining.slice(end + 2);
    } else {
      const end = remaining.indexOf("`", codeIdx + 1);
      if (end === -1) { parts.push(<span key={key++} className="break-words">{remaining}</span>); break; }
      parts.push(
        <code key={key++} className="bg-gray-100 text-red-600 rounded px-1 py-0.5 text-[13px] font-mono break-all">
          {remaining.slice(codeIdx + 1, end)}
        </code>
      );
      remaining = remaining.slice(end + 1);
    }
  }
  return <>{parts}</>;
}

function MessageContent({ text }) {
  if (!text) return null;

  // Split on fenced code blocks first
  const segments = [];
  const codeRe = /```(\w*)\n?([\s\S]*?)```/g;
  let last = 0;
  let m;
  while ((m = codeRe.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: "text", content: text.slice(last, m.index) });
    segments.push({ type: "code", lang: m[1] || "", content: m[2].trim() });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: "text", content: text.slice(last) });

  return (
    <div className="space-y-3 text-[15px] leading-relaxed text-gray-800 min-w-0 break-words [overflow-wrap:anywhere]">
      {segments.map((seg, si) => {
        if (seg.type === "code") {
          return (
            <div key={si} className="relative rounded-xl overflow-hidden border border-gray-200">
              <div className="flex items-center justify-between px-4 py-2 bg-gray-800 text-gray-400 text-xs font-mono">
                <span>{seg.lang || "code"}</span>
                <CopyButton text={seg.content} title="Salin kode" />
              </div>
              <pre className="bg-gray-900 text-gray-100 p-4 overflow-x-auto max-w-full text-sm font-mono leading-relaxed">
                <code>{seg.content}</code>
              </pre>
            </div>
          );
        }

        // Render text segment line by line
        const lines = seg.content.split("\n");
        const elements = [];
        let i = 0;
        while (i < lines.length) {
          const line = lines[i];
          if (line === "") { elements.push(<div key={`${si}-${i}`} className="h-2" />); i++; continue; }
          if (/^#{1,3} /.test(line)) {
            const lvl = (line.match(/^(#+) /)[1] || "").length;
            const txt = line.replace(/^#+\s/, "");
            const cls = lvl === 1 ? "font-bold text-xl mt-2 mb-1" : lvl === 2 ? "font-bold text-lg mt-2 mb-1" : "font-semibold mt-1 mb-0.5";
            elements.push(<p key={`${si}-${i}`} className={cls}><InlineText text={txt} /></p>);
            i++; continue;
          }
          if (/^[-*] /.test(line)) {
            const items = [];
            while (i < lines.length && /^[-*] /.test(lines[i])) {
              items.push(
                <p key={i} className="break-words [overflow-wrap:anywhere]">
                  <InlineText text={lines[i].slice(2)} />
                </p>
              );
              i++;
            }
            elements.push(<div key={`${si}-list-${i}`} className="space-y-1 my-1">{items}</div>);
            continue;
          }
          if (/^\d+\. /.test(line)) {
            const items = [];
            while (i < lines.length && /^\d+\. /.test(lines[i])) {
              const match = lines[i].match(/^(\d+)\.\s+(.*)$/);
              const itemNo = match ? Number(match[1]) : undefined;
              const itemText = match ? match[2] : lines[i].replace(/^\d+\. /, "");
              items.push(
                <li
                  key={i}
                  value={itemNo}
                  className="ml-5 list-decimal break-words [overflow-wrap:anywhere]"
                >
                  <InlineText text={itemText} />
                </li>
              );
              i++;
            }
            elements.push(<ol key={`${si}-ol-${i}`} className="space-y-1 my-1">{items}</ol>);
            continue;
          }
          elements.push(<p key={`${si}-${i}`} className="break-words [overflow-wrap:anywhere]"><InlineText text={line} /></p>);
          i++;
        }
        return <div key={si}>{elements}</div>;
      })}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 pb-32">
      <div className="w-16 h-16 rounded-2xl bg-[#10a37f]/10 flex items-center justify-center mb-4">
        <span className="material-symbols-outlined text-[#10a37f] text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>smart_toy</span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Jasmine</h2>
      <p className="text-gray-500 text-center max-w-sm mb-6">
        AI internal PT Sinar Jernih Suksesindo yang siap membantu menjawab, mencari, dan merangkum knowledge yang tersedia.
      </p>
      <button
        onClick={onNew}
        className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white font-semibold transition-colors"
      >
        <span className="material-symbols-outlined text-[18px]">add</span>
        Mulai Chat Baru
      </button>
    </div>
  );
}

export default function ChatView() {
  const { activeSessionId, messages, sendMessage, sending, createSession } = useOutletContext();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [useKnowledge, setUseKnowledge] = useState(true);
  const endRef = useRef(null);
  const textareaRef = useRef(null);
  const composerMinHeight = 48;
  const composerMaxHeight = 220;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  function autoResize(el = textareaRef.current) {
    if (!el) return;
    el.style.height = "auto";
    const nextHeight = Math.min(Math.max(el.scrollHeight, composerMinHeight), composerMaxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > composerMaxHeight ? "auto" : "hidden";
  }

  async function submit(e) {
    e?.preventDefault();
    if (!text.trim() || sending || !activeSessionId) return;
    const msg = text.trim();
    setText("");
    if (textareaRef.current) {
      textareaRef.current.style.height = `${composerMinHeight}px`;
      textareaRef.current.style.overflowY = "hidden";
    }
    try {
      await sendMessage({ message: msg, use_knowledge: useKnowledge, top_k: 8 });
    } catch (_) {
      setText(msg);
      setTimeout(() => autoResize(), 0);
    }
  }

  function handleComposerKeyDown(e) {
    if (!shouldSubmitOnEnter(e)) return;
    e.preventDefault();
    submit();
  }

  return (
    <main className="flex-1 flex flex-col min-h-0 bg-white overflow-x-hidden">
      {/* Top bar */}
      <header className="flex items-center justify-between h-14 pl-14 pr-4 lg:px-5 border-b border-gray-100 flex-shrink-0 bg-white">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-gray-900">Jasmine</h2>
          <span className="px-2 py-0.5 rounded-md bg-[#10a37f]/10 text-[#10a37f] text-[10px] font-bold uppercase tracking-wider">Internal</span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none group">
          <div className={`relative inline-flex w-9 h-5 rounded-full transition-colors ${useKnowledge ? "bg-[#10a37f]" : "bg-gray-200"}`}
            onClick={() => setUseKnowledge(!useKnowledge)}>
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${useKnowledge ? "translate-x-4" : "translate-x-0"}`} />
          </div>
          <span className="text-sm font-medium text-gray-600 group-hover:text-gray-900 transition-colors">Knowledge</span>
        </label>
      </header>

      {/* Messages area */}
      <section className="flex-1 overflow-y-auto overflow-x-hidden" id="chat-feed">
        {!activeSessionId && messages.length === 0 ? (
          <EmptyState onNew={createSession} />
        ) : (
          <div className="w-full max-w-[740px] mx-auto px-3 sm:px-4 py-6 space-y-1 overflow-x-hidden">
            {useKnowledge && activeSessionId && (
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#10a37f]/8 border border-[#10a37f]/20 text-[11px] text-[#10a37f] font-medium">
                  <span className="material-symbols-outlined text-[13px]">lock</span>
                  Knowledge aktif — jawaban mengikuti ACL &amp; classification
                </div>
              </div>
            )}

            {messages.map((msg) =>
              msg.role === "user" ? (
                // User message — right aligned
                <div key={msg.id} className="flex justify-end py-2">
                  <div className="max-w-[80%] group">
                    <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-tr-sm px-4 py-3 min-w-0">
                      <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</p>
                    </div>
                    <p className="text-[10px] text-gray-300 text-right mt-1 opacity-0 group-hover:opacity-100 transition-opacity">{fmtTime(msg.created_at)}</p>
                  </div>
                </div>
              ) : (
                // Assistant message — left aligned
                <div key={msg.id} className="flex gap-3 py-3 group">
                  <div className="w-8 h-8 rounded-lg bg-[#10a37f] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="material-symbols-outlined text-white text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>smart_toy</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-400 mb-1.5">Jasmine</p>
                    <MessageContent text={cleanAssistantText(msg.content)} />
                    <div className="mt-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <CopyButton text={cleanAssistantText(msg.content)} title="Salin jawaban" variant="light" />
                      <p className="text-[10px] text-gray-300">{fmtTime(msg.created_at)}</p>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Typing indicator */}
            {sending && (
              <div className="flex gap-3 py-3">
                <div className="w-8 h-8 rounded-lg bg-[#10a37f] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="material-symbols-outlined text-white text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>smart_toy</span>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-400 mb-1.5">Jasmine</p>
                  <TypingDots />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </section>

      {/* Composer */}
      <div className="flex-shrink-0 px-3 sm:px-4 pb-5 pt-3 bg-gradient-to-t from-white via-white to-white/0 overflow-x-hidden">
        <div className="w-full max-w-[740px] mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg focus-within:border-gray-300 focus-within:shadow-xl transition-all">
            <div className="flex items-end gap-2 p-2.5">
              {/* Attachment */}
              <button
                type="button"
                onClick={() => navigate("/knowledge")}
                className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
                title="Knowledge base"
              >
                <span className="material-symbols-outlined text-[20px]">library_books</span>
              </button>

              <textarea
                ref={textareaRef}
                className="flex-1 bg-transparent border-none focus:ring-0 outline-none text-gray-900 placeholder:text-gray-400 px-1 py-3 resize-none text-[15px] leading-relaxed max-h-[220px] scrollbar-thin"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  autoResize(e.target);
                }}
                onKeyDown={handleComposerKeyDown}
                placeholder={activeSessionId ? "Tulis pertanyaan..." : "Buat session baru untuk mulai chat"}
                disabled={!activeSessionId || sending}
                rows={1}
                style={{ height: composerMinHeight, overflowY: "hidden" }}
              />

              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => navigate("/voice")}
                  className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                  title="Voice AI"
                  disabled={!activeSessionId || sending}
                >
                  <span className="material-symbols-outlined text-[20px]">mic</span>
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!activeSessionId || sending || !text.trim()}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white transition-all disabled:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
                  title="Kirim"
                >
                  <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>arrow_upward</span>
                </button>
              </div>
            </div>
          </div>
          <p className="text-center text-[11px] text-gray-300 mt-2">
            Jawaban AI bisa salah. Verifikasi informasi penting sebelum digunakan.
          </p>
        </div>
      </div>
    </main>
  );
}
